/**
 * Importers: markdown files/zip (covers Obsidian), Notion export zip,
 * Trilium export zip, Evernote .enex. Every imported note becomes a FRESH
 * Y.Doc seeded once from text — the safe direction.
 */
import { unzipSync, strFromU8 } from "fflate";
import TurndownService from "turndown";
import { stripMarkdownEscapes } from "@hydra-notes/shared";
import { getData } from "./store";

export interface ImportResult {
  notes: number;
  folders: number;
  skipped: number;
  flavor: string;
}

async function createNoteWithContent(parentId: string | null, title: string, markdown: string): Promise<void> {
  const data = getData();
  const id = data.workspace.createItem("note", parentId, title);
  const handle = data.notes.open(id);
  await handle.whenLoaded;
  if (markdown) {
    handle.doc.transact(() => {
      handle.text.insert(0, markdown);
    });
  }
  handle.release();
}

/** Folder cache so "a/b/c.md" builds each folder once. */
class FolderBuilder {
  private cache = new Map<string, string>();
  created = 0;
  constructor(private rootParent: string | null) {}

  ensure(pathParts: string[]): string | null {
    let parent = this.rootParent;
    let key = "";
    for (const part of pathParts) {
      key = key ? `${key}/${part}` : part;
      const hit = this.cache.get(key);
      if (hit) {
        parent = hit;
        continue;
      }
      const data = getData();
      const existing = data.workspace
        .childrenOf(parent)
        .find((i) => i.type === "folder" && i.title === part);
      const id = existing?.id ?? data.workspace.createItem("folder", parent, part);
      if (!existing) this.created++;
      this.cache.set(key, id);
      parent = id;
    }
    return parent;
  }
}

const NOTION_SUFFIX = /\s+[0-9a-f]{32}(?=(\.md$|\/|$))/g;

function detectZipFlavor(paths: string[]): "trilium" | "notion" | "markdown" {
  if (paths.some((p) => p.endsWith("!!!meta.json"))) return "trilium";
  if (paths.some((p) => NOTION_SUFFIX.test(p))) return "notion";
  return "markdown";
}

export async function importZip(buffer: ArrayBuffer, parentId: string | null): Promise<ImportResult> {
  const entries = unzipSync(new Uint8Array(buffer));
  const paths = Object.keys(entries);
  const flavor = detectZipFlavor(paths);
  const folders = new FolderBuilder(parentId);
  let notes = 0;
  let skipped = 0;

  for (const [rawPath, bytes] of Object.entries(entries)) {
    if (rawPath.endsWith("/")) continue;
    let path = rawPath;
    if (flavor === "notion") path = path.replace(NOTION_SUFFIX, "");
    if (flavor === "trilium" && path.endsWith("!!!meta.json")) continue;

    if (!/\.(md|markdown|txt)$/i.test(path)) {
      skipped++;
      continue;
    }
    const parts = path.split("/").filter(Boolean);
    const fileName = parts.pop()!;
    const title = fileName.replace(/\.(md|markdown|txt)$/i, "");
    const folderId = parts.length ? folders.ensure(parts) : parentId;
    let content = strFromU8(bytes);
    // HTML->md exporters (Trilium, Notion) litter defensive backslash
    // escapes ("1\.", "\-"); strip them so notes read clean
    if (flavor === "trilium" || flavor === "notion") content = stripMarkdownEscapes(content);
    await createNoteWithContent(folderId, title, content);
    notes++;
  }
  return { notes, folders: folders.created, skipped, flavor };
}

export async function importMarkdownFiles(files: File[], parentId: string | null): Promise<ImportResult> {
  let notes = 0;
  for (const file of files) {
    const title = file.name.replace(/\.(md|markdown|txt)$/i, "");
    await createNoteWithContent(parentId, title, await file.text());
    notes++;
  }
  return { notes, folders: 0, skipped: 0, flavor: "markdown" };
}

export async function importEnex(xml: string, parentId: string | null): Promise<ImportResult> {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  const turndown = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced", bulletListMarker: "-" });
  turndown.addRule("en-media", {
    filter: (node) => node.nodeName.toLowerCase() === "en-media",
    replacement: () => "*(attachment not imported)*",
  });
  turndown.addRule("en-todo", {
    filter: (node) => node.nodeName.toLowerCase() === "en-todo",
    replacement: (_c, node) =>
      (node as HTMLElement).getAttribute("checked") === "true" ? "[x] " : "[ ] ",
  });

  const folderId = getData().workspace.createItem("folder", parentId, "Evernote import");
  let notes = 0;
  let skipped = 0;
  for (const noteEl of [...doc.querySelectorAll("note")]) {
    const title = noteEl.querySelector("title")?.textContent ?? "Untitled";
    const enml = noteEl.querySelector("content")?.textContent ?? "";
    try {
      const inner = new DOMParser().parseFromString(enml, "text/html").body;
      const markdown = stripMarkdownEscapes(turndown.turndown(inner.innerHTML));
      await createNoteWithContent(folderId, title, markdown);
      notes++;
    } catch {
      skipped++;
    }
  }
  return { notes, folders: 1, skipped, flavor: "evernote" };
}

/** Route a picked file to the right importer by extension/shape. */
export async function importFile(file: File, parentId: string | null = null): Promise<ImportResult> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".enex")) return importEnex(await file.text(), parentId);
  if (name.endsWith(".zip")) return importZip(await file.arrayBuffer(), parentId);
  if (/\.(md|markdown|txt)$/.test(name)) return importMarkdownFiles([file], parentId);
  throw new Error("Unsupported file type. Use .md, .zip, or .enex.");
}
